import { createFileRoute, Link } from "@tanstack/react-router"
import { parseMarketplaceId, type MarketplaceId } from "@nova-ai/shared"
import { ChevronLeft } from "lucide-react"
import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { OrderHeader } from "../components/orders/order-header"
import { OrderNav } from "../components/orders/order-nav"
import { OrderStatusPanels } from "../components/orders/order-action-card"
import { OrderSummary } from "../components/orders/order-summary"
import { OrderJourney } from "../components/orders/order-journey"
import { OrderItems } from "../components/orders/order-items"
import { useOrder } from "../services/use-orders"
import { useRates } from "../services/use-rates"
import { ordersSearch, safeParseMarketplaceId } from "./orders.index"

function OrderDetailPage() {
  const params = Route.useParams()
  const search = Route.useSearch()
  const marketplace = parseMarketplaceId(params.marketplace)
  const order = useOrder(marketplace, params.orderId)
  const rates = useRates()
  const marketplaces = (search.marketplace ?? [])
    .map(safeParseMarketplaceId)
    .filter((value): value is MarketplaceId => value !== null)
  const data = order.data
  return (
    <AppShell title={`Order ${params.orderId}`} hideTitle>
      <QueryState isLoading={order.isLoading} error={order.error} isEmpty={!data}>
        {data ? (
          <div
            key={`${marketplace}:${params.orderId}`}
            className="order-detail mx-auto max-w-[1160px] space-y-6 [overflow-wrap:anywhere] [&_button]:min-h-11 [&_button]:min-w-11 [&_button]:focus-visible:outline-2 [&_button]:focus-visible:outline-offset-2 [&_button]:focus-visible:outline-ring sm:[&_button]:min-h-0 sm:[&_button]:min-w-0"
          >
            <Link
              to="/orders"
              search={search}
              className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            >
              <ChevronLeft className="size-4" /> Orders
            </Link>

            <OrderHeader
              data={data}
              nav={
                <OrderNav
                  current={{ marketplace, orderId: params.orderId }}
                  marketplaces={marketplaces}
                  state={search.state ?? "all"}
                  query={search.q}
                />
              }
            />

            <div className="grid min-w-0 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
              {data.state === "blocked" ||
              data.problems.length > 0 ||
              data.resolution.resolvedAt ||
              data.addressCorrection ? (
                <div className="min-w-0 space-y-4 xl:col-start-1 xl:row-start-1">
                  <OrderStatusPanels data={data} />
                </div>
              ) : null}

              <aside className="min-w-0 xl:sticky xl:top-6 xl:col-start-2 xl:row-span-3 xl:row-start-1">
                <OrderSummary data={data} rates={rates.data} />
              </aside>

              <div className="min-w-0 xl:col-start-1">
                <OrderJourney data={data} />
              </div>

              <div className="min-w-0 xl:col-start-1">
                <OrderItems data={data} rates={rates.data} />
              </div>
            </div>
          </div>
        ) : null}
      </QueryState>
    </AppShell>
  )
}

export const Route = createFileRoute("/orders/$marketplace/$orderId")({
  validateSearch: ordersSearch,
  component: OrderDetailPage,
})
